import { useMemo } from 'react';
import { Bot, Coins, Database, Gauge } from 'lucide-react';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import type { AppUsageAiResponse } from '../../types/appUsage';
import { fmtNum, fmtChf, fmtMTok } from './format';

interface Props {
    ai: AppUsageAiResponse | null;
}

/** Stable categorical palette for the stacked daily-cost chart. */
const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#9ca3af'];
const MAX_STACKED_FEATURES = 6;

export function AppUsageAiTab({ ai }: Props) {
    // Pivot day×feature cost rows into one row per day with the top features as
    // keys (rest bucketed as 'other') — Recharts stacks by key.
    const { chartData, chartFeatures } = useMemo(() => {
        if (!ai) return { chartData: [], chartFeatures: [] as string[] };
        const totalsByFeature = new Map<string, number>();
        for (const r of ai.dailyByFeature) {
            totalsByFeature.set(r.feature, (totalsByFeature.get(r.feature) ?? 0) + r.costChf);
        }
        const top = [...totalsByFeature.entries()]
            .sort((a, b) => b[1] - a[1])
            .slice(0, MAX_STACKED_FEATURES)
            .map(([f]) => f);
        const topSet = new Set(top);
        const hasOther = totalsByFeature.size > top.length;

        const byDay = new Map<string, Record<string, number | string>>();
        for (const r of ai.dailyByFeature) {
            let row = byDay.get(r.day);
            if (!row) { row = { day: r.day }; byDay.set(r.day, row); }
            const key = topSet.has(r.feature) ? r.feature : 'other';
            row[key] = ((row[key] as number) ?? 0) + r.costChf;
        }
        return {
            chartData: [...byDay.values()].sort((a, b) => String(a.day).localeCompare(String(b.day))),
            chartFeatures: hasOther ? [...top, 'other'] : top,
        };
    }, [ai]);

    if (!ai) return <p className="text-sm text-gray-500">Loading…</p>;
    const t = ai.totals;

    const tiles = [
        { label: 'AI cost (CHF)', icon: Coins, color: 'text-purple-600', value: fmtChf(t.costChf), sub: `USD ${fmtChf(t.costUsd)}` },
        { label: 'Calls', icon: Bot, color: 'text-blue-600', value: fmtNum(t.calls), sub: `${fmtNum(t.errorCalls)} errors` },
        { label: 'Tokens in / out', icon: Database, color: 'text-gray-600', value: `${fmtMTok(t.inputTokens)} / ${fmtMTok(t.outputTokens)}`, sub: `${fmtMTok(t.cacheReadTokens)} cache reads` },
        { label: 'Cache hit', icon: Gauge, color: 'text-green-600', value: t.cacheHitPct === null ? '—' : `${t.cacheHitPct}%`, sub: 'cacheRead / (input + cacheRead)' },
    ];

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {tiles.map(tile => {
                    const Icon = tile.icon;
                    return (
                        <div key={tile.label} className="rounded-lg border border-gray-200 bg-white p-3">
                            <div className="flex items-center gap-2">
                                <Icon className={`h-4 w-4 ${tile.color}`} />
                                <span className="text-xs font-medium text-gray-500">{tile.label}</span>
                            </div>
                            <p className="mt-1 text-lg font-bold text-gray-900">{tile.value}</p>
                            <p className="text-xs text-gray-500">{tile.sub}</p>
                        </div>
                    );
                })}
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">Daily AI cost by feature (CHF) — UTC</h4>
                <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                            <XAxis dataKey="day" tickFormatter={(d: string) => d.slice(5)} interval="preserveStartEnd" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v) => `CHF ${fmtChf(Number(v ?? 0))}`} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {chartFeatures.map((f, i) => (
                                <Bar key={f} dataKey={f} name={f} stackId="cost" fill={COLORS[i % COLORS.length]} />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Feature</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Model</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Calls</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Errors</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Tokens in</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Tokens out</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Cache read</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">CHF</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Ø ms</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {ai.featureModel.map(f => (
                            <tr key={`${f.feature}|${f.model}`} className="hover:bg-gray-50">
                                <td className="px-3 py-2 font-mono text-xs text-gray-800">{f.feature}</td>
                                <td className="px-3 py-2 font-mono text-xs text-gray-500">{f.model}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{fmtNum(f.calls)}</td>
                                <td className={`px-3 py-2 text-right tabular-nums ${f.errorCalls > 0 ? 'text-red-600' : 'text-gray-400'}`}>{fmtNum(f.errorCalls)}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{fmtMTok(f.inputTokens)}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{fmtMTok(f.outputTokens)}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{fmtMTok(f.cacheReadTokens)}</td>
                                <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtChf(f.costChf)}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-gray-500">{f.avgDurationMs === null ? '—' : fmtNum(f.avgDurationMs)}</td>
                            </tr>
                        ))}
                        {ai.featureModel.length === 0 && (
                            <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-500">No AI calls in this window.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">Top users by AI cost (window)</h4>
                <ul className="space-y-0.5 text-sm">
                    {ai.topUsers.map(u => (
                        <li key={u.email} className="flex justify-between gap-2">
                            <span className="truncate text-gray-800">{u.email}</span>
                            <span className="whitespace-nowrap tabular-nums text-gray-500">
                                {fmtNum(u.calls)} calls · CHF {fmtChf(u.costChf)}
                            </span>
                        </li>
                    ))}
                    {ai.topUsers.length === 0 && <li className="text-gray-500">No AI calls in this window.</li>}
                </ul>
            </div>
        </div>
    );
}
